import React, { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { showError, showSuccess, showInfo } from '../utils/notifications';

const ShoppingList = () => {
  const [shoppingList, setShoppingList] = useState([]);
  const [newItem, setNewItem] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Charger la liste de courses depuis le localStorage au démarrage
  useEffect(() => {
    const savedList = localStorage.getItem('shoppingList');
    if (savedList) {
      try {
        setShoppingList(JSON.parse(savedList));
      } catch (error) {
        console.error('Erreur lors du chargement de la liste:', error);
      }
    }
  }, []);

  // Sauvegarder la liste dans le localStorage à chaque modification
  useEffect(() => {
    localStorage.setItem('shoppingList', JSON.stringify(shoppingList));
  }, [shoppingList]);

  const addItem = () => {
    if (!newItem.trim()) {
      showError('Veuillez entrer un nom d\'article');
      return;
    }

    const item = {
      id: Date.now(),
      name: newItem.trim(),
      completed: false,
      addedAt: new Date().toISOString()
    };

    setShoppingList([...shoppingList, item]);
    setNewItem('');
    showSuccess('Article ajouté à la liste');
  };

  const toggleItem = (id) => {
    setShoppingList(shoppingList.map(item =>
      item.id === id ? { ...item, completed: !item.completed } : item
    ));
  };

  const removeItem = (id) => {
    setShoppingList(shoppingList.filter(item => item.id !== id));
    showInfo('Article supprimé de la liste');
  };

  const clearCompleted = () => {
    const completedCount = shoppingList.filter(item => item.completed).length;
    if (completedCount === 0) {
      showInfo('Aucun article coché à supprimer');
      return;
    }
    
    setShoppingList(shoppingList.filter(item => !item.completed));
    showSuccess(`${completedCount} article(s) supprimé(s)`);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      addItem();
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white min-h-screen">
      {/* Header */}
      <div className="bg-primary text-white p-4">
        <h1 className="text-xl font-bold text-center">Ma Liste de Courses</h1>
        <p className="text-center text-purple-100 text-sm mt-1">
          {shoppingList.length} article(s) - {shoppingList.filter(item => item.completed).length} coché(s)
        </p>
      </div>

      {/* Ajouter un article */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex gap-2">
          <input
            type="text"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ajouter un article..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <button
            onClick={addItem}
            disabled={isLoading}
            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
          >
            <PlusIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Liste des articles */}
      <div className="p-4">
        {shoppingList.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-2">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
            <p className="text-gray-500">Votre liste de courses est vide</p>
            <p className="text-gray-400 text-sm">Ajoutez votre premier article ci-dessus</p>
          </div>
        ) : (
          <div className="space-y-2">
            {shoppingList.map((item) => (
              <div
                key={item.id}
                className={`flex items-center gap-3 p-3 rounded-lg border ${
                  item.completed 
                    ? 'bg-gray-50 border-gray-200' 
                    : 'bg-white border-gray-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={item.completed}
                  onChange={() => toggleItem(item.id)}
                  className="w-5 h-5 text-primary rounded focus:ring-primary"
                />
                <span
                  className={`flex-1 ${
                    item.completed 
                      ? 'text-gray-500 line-through' 
                      : 'text-gray-900'
                  }`}
                >
                  {item.name}
                </span>
                <button
                  onClick={() => removeItem(item.id)}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Bouton pour supprimer les articles cochés */}
        {shoppingList.some(item => item.completed) && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={clearCompleted}
              className="w-full bg-red-100 text-red-700 py-2 px-4 rounded-lg hover:bg-red-200 transition-colors"
            >
              Supprimer les articles cochés
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShoppingList; 